import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { RepairJob, JobStatus } from '../types';

interface CalendarViewProps {
  jobs: RepairJob[];
  onSelectJob: (job: RepairJob) => void;
  userRoleType: 'CUSTOMER' | 'SHOP';
}

export const CalendarView: React.FC<CalendarViewProps> = ({ jobs, onSelectJob, userRoleType }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();

  const getJobsForDate = (day: number) => {
    const dateStr = `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => job.scheduledDate === dateStr);
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.PENDING: return 'bg-amber-400';
      case JobStatus.CONFIRMED: return 'bg-blue-500';
      case JobStatus.IN_PROGRESS: return 'bg-violet-500';
      case JobStatus.COMPLETED: return 'bg-emerald-500';
      case JobStatus.REJECTED: return 'bg-red-400';
      case JobStatus.CANCELLED: return 'bg-slate-400';
      default: return 'bg-slate-300';
    }
  };

  return (
    <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between border-b border-slate-50">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="text-violet-600" size={20} />
          <h2 className="text-xl font-black text-slate-900">{monthName} {year}</h2>
        </div>
        <div className="flex space-x-2">
          <button onClick={handlePrevMonth} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-500">
            <ChevronLeft size={20} />
          </button>
          <button onClick={handleNextMonth} className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-500">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Grid Header */}
      <div className="grid grid-cols-7 border-b border-slate-50 bg-slate-50/50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>

      {/* Grid Body */}
      <div className="grid grid-cols-7 auto-rows-fr">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-slate-50/50 bg-slate-50/20" />
        ))}
        
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayJobs = getJobsForDate(day);
          const isToday = new Date().toDateString() === new Date(year, currentDate.getMonth(), day).toDateString();

          return (
            <div key={day} className={`min-h-[100px] p-2 border-b border-r border-slate-50 relative group transition-colors hover:bg-violet-50/30 ${isToday ? 'bg-violet-50/20' : ''}`}>
              <div className={`text-sm font-bold mb-2 flex justify-between items-center ${isToday ? 'text-violet-600' : 'text-slate-400'}`}>
                <span className={isToday ? 'bg-violet-100 px-2 py-0.5 rounded-lg' : ''}>{day}</span>
                {dayJobs.length > 0 && (
                   <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded-md md:hidden">
                     {dayJobs.length}
                   </span>
                )}
              </div>
              
              <div className="space-y-1">
                {dayJobs.map(job => (
                  <button
                    key={job.id}
                    onClick={() => onSelectJob(job)}
                    className="w-full text-left bg-white border border-slate-100 p-1.5 rounded-lg shadow-sm hover:shadow-md hover:border-violet-200 transition-all group/item overflow-hidden"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                       <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(job.status)}`} />
                       <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate">
                         {job.status}
                       </span>
                    </div>
                    <div className="text-xs font-bold text-slate-800 truncate leading-tight">
                       {userRoleType === 'SHOP' ? job.customerName : job.shopName}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate mt-0.5">
                       {job.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};